import * as THREE from 'three';
import { rngFromString, type PlanetDef, type PlanetSampler, type TerrainSample } from '@hyperlight/shared';
import { groundBeach, groundForest, groundSnow } from '../groundSurface';
import { groundPatchAt } from '../groundDetail';
import { clearingAt } from './field';
import { floraTime, floraHeight } from './material';
import { makePooledFloraMesh } from './materialPool';
import { prepareScatterBounds } from './scatterLod';
import type { ClutterSets } from './clutter';
import { faceDir } from '../cubesphere';
import {canyonCoverSuppression} from './canyonField';

export interface FloorAnchor { id: string; x: number; y: number; z: number; radius: number; kind: 'tree' | 'rock' }
const CELL = 32;
const key = (x:number,y:number,z:number) => `${x},${y},${z}`;
/** Independent of worker completion order. Only nearby anchors are queried;
 * owners unregister when their terrain node dies, including settlement restitches. */
export class ForestFloorIndex<Owner = object> {
  revision = 0;
  private owners = new Map<Owner, FloorAnchor[]>();
  private cells = new Map<string, Map<string,FloorAnchor>>();
  set(owner: Owner, anchors: FloorAnchor[]): void {
    this.delete(owner);
    if (!anchors.length) return;
    this.owners.set(owner,anchors);
    for (const a of anchors) {
      const k = key(Math.floor(a.x/CELL),Math.floor(a.y/CELL),Math.floor(a.z/CELL));
      let cell = this.cells.get(k);
      if (!cell) this.cells.set(k,cell = new Map());
      cell.set(a.id,a);
    }
    this.revision++;
  }
  delete(owner: Owner): void {
    const anchors = this.owners.get(owner);
    if (!anchors) return;
    for (const a of anchors) {
      const k = key(Math.floor(a.x/CELL),Math.floor(a.y/CELL),Math.floor(a.z/CELL));
      const cell = this.cells.get(k)!; cell.delete(a.id);
      if (!cell.size) this.cells.delete(k);
    }
    this.owners.delete(owner); this.revision++;
  }
  query(center: THREE.Vector3, radius: number): FloorAnchor[] {
    const out: FloorAnchor[] = [];
    for (let x=Math.floor((center.x-radius)/CELL);x<=Math.floor((center.x+radius)/CELL);x++)
      for (let y=Math.floor((center.y-radius)/CELL);y<=Math.floor((center.y+radius)/CELL);y++)
        for (let z=Math.floor((center.z-radius)/CELL);z<=Math.floor((center.z+radius)/CELL);z++) {
          for (const a of this.cells.get(key(x,y,z))?.values() ?? [])
            if (Math.hypot(a.x-center.x,a.y-center.y,a.z-center.z)<=radius) out.push(a);
        }
    return out.sort((a,b)=>a.id.localeCompare(b.id));
  }
}

type Kind = 'fern'|'shrub'|'moss'|'litter';
export const FLOOR_LIMITS = { fern: 48, shrub: 16, moss: 44, litter: 48 } as const;
interface FloorTile { face:number; level:number; u0:number; v0:number; size:number }
const sample = (): TerrainSample => ({h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8});

/** Footprint-aware exclusions: low growth must not hide paths, occupy a town,
 * float over a channel, or put green leaves through snow. */
export function floorAllowed(ts: TerrainSample, def: PlanetDef, amplitude:number, footprint:number): boolean {
  const height = ts.h*amplitude;
  return ts.town<=.002 && ts.road>=2.2+footprint && ts.river<=.18
    && (ts.riverWs??-1e9)<ts.h-.65/amplitude
    && (ts.wetlandWaterM??-1e9)<height-.15
    && groundSnow(ts.temp,height,def.climate.snowline*amplitude)<.12
    && (def.seaRadius===null || groundBeach(def.radius+height-def.seaRadius)<.05)
    && ts.temp>-.42 && ts.temp<.8 && ts.moist>-.05;
}

/** Near-tile ownership bounds draw cost and lifetime. Candidate seeds belong to
 * anchors, so neighboring tiles split the same cluster without duplicates. */
export function buildForestFloor(def:PlanetDef, sampler:PlanetSampler, clutter:ClutterSets,
  tile:FloorTile, anchors:FloorAnchor[], clearingFreq:number, maturitySeed:number): THREE.InstancedMesh[] {
  const seed = `${def.seed}:floor:${tile.face}:${tile.level}:${tile.u0}:${tile.v0}`;
  const tileRng = rngFromString(seed), ts=sample(), probeTs=sample();
  const up=new THREE.Vector3(), pos=new THREE.Vector3(), ex=new THREE.Vector3(), ez=new THREE.Vector3();
  const normal=new THREE.Vector3(), q=new THREE.Quaternion(), spin=new THREE.Quaternion(), matrix=new THREE.Matrix4();
  const Y=new THREE.Vector3(0,1,0), X=new THREE.Vector3(1,0,0), probe=new THREE.Vector3();
  const transforms:Record<Kind,THREE.Matrix4[]>={fern:[],shrub:[],moss:[],litter:[]};
  const accepted:Record<Kind,number>={fern:0,shrub:0,moss:0,litter:0};
  const evidence:Record<Kind,object[]>={fern:[],shrub:[],moss:[],litter:[]};
  // Each cube face uses two signed coordinates divided by its dominant axis.
  // Explicit ownership avoids an anchor straddling two tiles being emitted twice.
  const owns = (p:THREE.Vector3):boolean => {
    const a=[Math.abs(p.x),Math.abs(p.y),Math.abs(p.z)];
    const face=a[0]>=a[1]&&a[0]>=a[2]?(p.x>0?0:1):a[1]>=a[2]?(p.y>0?2:3):(p.z>0?4:5);
    if(face!==tile.face)return false;
    // Invert faceDir's basis, checked against the production cube mapping.
    const k=a[Math.floor(face/2)];
    const uv=face===0?[-p.z,p.y]:face===1?[p.z,p.y]:face===2?[p.x,-p.z]:face===3?[p.x,p.z]:face===4?[p.x,p.y]:[-p.x,p.y];
    const u=uv[0]/k,v=uv[1]/k;
    return u>=tile.u0&&u<tile.u0+tile.size&&v>=tile.v0&&v<tile.v0+tile.size;
  };
  const emit = (kind:Kind, point:THREE.Vector3, size:number, yaw:number, source:string):void => {
    if(accepted[kind]>=FLOOR_LIMITS[kind])return;
    up.copy(point).normalize(); if(!owns(up))return;
    sampler.sample(up.x,up.y,up.z,ts,true);
    const footprint=size*(kind==='shrub'?1.1:kind==='fern'?.9:.62);
    if(!floorAllowed(ts,def,sampler.amplitude,footprint))return;
    const forest=groundForest(ts.temp,ts.moist);
    if(forest<.2)return;
    if(clearingAt(up.x,up.y,up.z,clearingFreq,maturitySeed^0x3517)>.64)return;
    const radius=def.radius+ts.h*sampler.amplitude;
    const patch=groundPatchAt(up.x*radius,up.y*radius,up.z*radius);
    if(kind==='moss'&&(ts.moist<.28||patch>.64))return;
    // Fit the ground under the whole footprint, rejecting ledges instead of
    // lifting a rigid carpet above the soil or burying half of a fern.
    ex.crossVectors(up,Math.abs(up.y)<.9?Y:X).normalize(); ez.crossVectors(up,ex);
    const heights:number[]=[];
    for(const [x,z] of [[1,0],[-1,0],[0,1],[0,-1]]) {
      probe.copy(up).addScaledVector(ex,x*footprint/radius).addScaledVector(ez,z*footprint/radius).normalize();
      sampler.sample(probe.x,probe.y,probe.z,probeTs,true);
      if(!floorAllowed(probeTs,def,sampler.amplitude,0))return;
      heights.push(def.radius+probeTs.h*sampler.amplitude-radius);
    }
    const gx=(heights[0]-heights[1])/(2*footprint), gz=(heights[2]-heights[3])/(2*footprint);
    const error=Math.max(Math.abs(heights[0]+heights[1]),Math.abs(heights[2]+heights[3]));
    if(Math.hypot(gx,gz)>.7||error>(kind==='litter'||kind==='moss'?.035:.18))return;
    accepted[kind]++;
    const canyon=canyonCoverSuppression(ts);
    if(canyon>0&&rngFromString(`${def.seed}:canyon-floor-replace:${kind}:${up.x}:${up.y}:${up.z}`)()<canyon)return;
    normal.copy(up).addScaledVector(ex,-gx).addScaledVector(ez,-gz).normalize();
    q.setFromUnitVectors(Y,normal).multiply(spin.setFromAxisAngle(Y,yaw));
    pos.copy(up).multiplyScalar(radius+(kind==='litter'?.003:-.018));
    matrix.compose(pos,q,new THREE.Vector3(size,size,size)); transforms[kind].push(matrix.clone());
    evidence[kind].push({source,x:pos.x,y:pos.y,z:pos.z,temp:ts.temp,road:ts.road,town:ts.town,river:ts.river,error});
  };
  for(const anchor of anchors) {
    const rng=rngFromString(`${def.seed}:floor-anchor:${anchor.id}`);
    const base=new THREE.Vector3(anchor.x,anchor.y,anchor.z), axis=base.clone().normalize();
    const tx=new THREE.Vector3().crossVectors(axis,Math.abs(axis.y)<.9?Y:X).normalize();
    const tz=new THREE.Vector3().crossVectors(axis,tx);
    const phase=rng()*Math.PI*2;
    for(let group=0;group<2;group++) {
      const angle=phase+group*(1.8+rng()), distance=anchor.radius+.65+rng()*1.5;
      const center=base.clone().addScaledVector(tx,Math.cos(angle)*distance).addScaledVector(tz,Math.sin(angle)*distance);
      for(let j=0;j<12;j++) {
        const kind:Kind=j<3?'shrub':j<6?'fern':j<9?'moss':'litter';
        const a=rng()*Math.PI*2, r=Math.sqrt(rng())*(kind==='shrub'?.85:1.25);
        const p=center.clone().addScaledVector(tx,Math.cos(a)*r).addScaledVector(tz,Math.sin(a)*r);
        if(p.distanceTo(base)<anchor.radius+.35)continue;
        emit(kind,p,kind==='fern'?.65+rng()*.4:kind==='shrub'?.85+rng()*.35:.8+rng()*.5,rng()*Math.PI*2,anchor.id);
      }
    }
  }
  // Moist bank pockets also work where the river opens out beyond the canopy.
  // PlanetSampler already enriches bank moisture; never infer water from color.
  const dir:number[]=[0,0,0];
  for(let i=0;i<12;i++) {
    faceDir(tile.face,tile.u0+tileRng()*tile.size,tile.v0+tileRng()*tile.size,dir);
    const base=new THREE.Vector3(...dir);sampler.sample(base.x,base.y,base.z,ts,true);
    if(ts.moist<.42)continue;
    const rad=def.radius+ts.h*sampler.amplitude;base.multiplyScalar(rad);
    if(groundPatchAt(base.x,base.y,base.z)>.42)continue;
    for(let j=0;j<4;j++) {
      const p=base.clone().add(new THREE.Vector3(tileRng()-.5,tileRng()-.5,tileRng()-.5).multiplyScalar(2));
      emit(j===0?'shrub':j===3?'moss':'fern',p,.7+tileRng()*.4,tileRng()*Math.PI*2,'moist-pocket');
    }
  }
  const meshes:THREE.InstancedMesh[]=[];
  for(const kind of ['fern','shrub','moss','litter'] as const) {
    const variant=accepted[kind]>0?Math.floor(tileRng()*4):0;
    if(!transforms[kind].length)continue;
    const geo=clutter[kind][variant], rigid=kind==='moss'||kind==='litter';
    const fadeFar=rigid?65:130;
    const mesh=makePooledFloraMesh(geo,transforms[kind].length,{birth:floraTime(),fadeFar,height:floraHeight(geo),rigid});
    transforms[kind].forEach((m,i)=>mesh.setMatrixAt(i,m));mesh.instanceMatrix.needsUpdate=true;
    if(!rigid)for(let i=0;i<evidence[kind].length;i++) {
      const cold=(evidence[kind][i] as {temp:number}).temp<-.3;
      mesh.setColorAt(i,new THREE.Color().setRGB(cold?1.10:1,cold?.85:1,cold?.65:1));
    }
    mesh.userData={forestFloor:kind,fadeFar,rigid,floorEvidence:evidence[kind]};
    prepareScatterBounds(mesh);mesh.receiveShadow=true;mesh.castShadow=false;meshes.push(mesh);
  }
  return meshes;
}
